import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ShoppingBag, Rocket, Users, Globe, MessageSquare, TrendingUp, Award } from 'lucide-react';

const AboutPage = () => {
  const containerRef = useRef(null);
  const statsRef = useRef(null);
  const quoteRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const isStatsInView = useInView(statsRef, { once: true, margin: "-100px" });
  const isQuoteInView = useInView(quoteRef, { once: true, margin: "-100px" });

  const y = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const stats = [
    { icon: Globe, value: "30+", label: "Years Experience" },
    { icon: ShoppingBag, value: "100+", label: "E-commerce Stores" },
    { icon: Users, value: "1000+", label: "Happy Clients" },
    { icon: TrendingUp, value: "∞", label: "Growth Potential" }
  ];

  const services = [
    "E-commerce Development",
    "SEO & Content Marketing",
    "Paid Advertising",
    "Product Placement",
    "Wholesale Integration",
    "Brand Development"
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen bg-[#b3ebf2] overflow-hidden">
      {/* Animated background elements */}
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-24"
        >
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="bg-[#f4b400] bg-clip-text text-transparent inline-block p-2 leading-relaxed bg-[length:100%_120%] bg-bottom">
              groviyo
            </span>
          </h1>
          <div className="max-w-3xl mx-auto">
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
              Sometimes there is power in small numbers.
            </p>
          </div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          ref={statsRef}
          initial={{ opacity: 0 }}
          animate={isStatsInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isStatsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl hover:shadow-lg transition-all duration-300"
            >
              <stat.icon className="w-8 h-8 mx-auto mb-4 text-[#f4b400]" />
              <div className="text-3xl font-bold mb-2 text-gray-800">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-16 mb-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Our Story</h2>
            <p className="text-gray-600 leading-relaxed">
              We are a small Virginia Beach based digital marketing agency specializing in e-commerce store development and traffic generation through SEO, Content Marketing, Paid Ads, and Product Placement.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our out-of-the-box approach ensures your business isn't just another site in the sea of competition but a brand people remember—and come back to.
            </p>
            <div className="pt-6 bg-white/80 backdrop-blur-sm rounded-2xl p-6">
              <Award className="w-12 h-12 text-[#f4b400] mb-4" />
              <p className="text-xl font-semibold text-gray-800">
                The secret sauce for retailers
              </p>
              <p className="text-gray-600">
                30+ years of experience in seamlessly integrating Wholesale Sales & Fulfillment Management online
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Our Expertise</h3>
            <div className="grid grid-cols-2 gap-4">
              {services.map((service, index) => (
                <motion.div
                  key={service}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center space-x-2"
                >
                  <Rocket className="w-4 h-4 text-[#f4b400]" />
                  <span className="text-gray-600">{service}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Quote Section */}
        <motion.div
          ref={quoteRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isQuoteInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto mb-24"
        >
          <div className="relative p-8 bg-white/80 backdrop-blur-sm rounded-2xl">
            <MessageSquare className="absolute -top-6 -left-6 w-12 h-12 text-[#f4b400]" />
            <blockquote className="text-xl md:text-2xl italic text-gray-600 mb-4">
              "Maecenas tempus ultricies nisi. Eget dui. Etiam rhoncus."
            </blockquote>
            <cite className="text-sm text-gray-500 not-italic">
              — Ross Drimmer, Welt Agency
            </cite>
          </div>
        </motion.div>

        {/* Final Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">We are GROVIYO</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              At Groviyo, we take the complexity out of online marketing, so you can focus on what you do best—running your business. From setting up high-converting stores to crafting messages that speak to your customers, we've mastered the art of digital growth.
            </p>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-12"
            >
              <div className="text-sm text-gray-500 uppercase tracking-wider mb-2">Amazing clients</div>
              <div className="text-2xl font-bold text-[#f4b400]">
                You handle your business, we'll handle your growth.
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutPage;